class CrossSellManager {
  // Constantes para selectores y claves de almacenamiento
  SELECTORS = {
    jsonFamilies: "#ph-mac133-json",
    jsonTranslations: "#ph-mac133-json-translates",
    controlCrossSell: "#corner-cowi-related-items",
    cartItemListWrapper: "#corner-cowi-cart-item-list-wrapper",
    cartItemList: "#corner-cowi-cart-item-list",
    cartTrigger: "#corner-cowi-open-primary-card",
    countdownDisplay: "#ph-mac-countdown",
    bundleAddBtn: ".ph-mac-bundle-add-btn"
  };
  STORAGE_KEYS = {
    countdownSeconds: 'ph_mac_countdown_seconds',
    countdownInitialized: 'ph_mac_countdown_initialized',
  };
  COUNTDOWN_DURATION = 180;

  // Estado de la instancia
  #phMacFamilies = [];
  #phMacTranslations = {};
  #phMacCrossSellContainer = null;
  #cartObserver = null;
  #containerObserver = null;
  #triggerObserver = null;
  #debounceTimer = null;
  #previousItems = [];
  #periodicCheckInterval = null;
  #countdownTimer = null;
  #countdownSeconds = this.COUNTDOWN_DURATION;
  #countdownInitialized = false;
  #controlCrossSellHideObserver = null;

  constructor() {
    try {
      this.#phMacFamilies = JSON.parse(document.querySelector(this.SELECTORS.jsonFamilies)?.textContent || '[]');
      this.#phMacTranslations = JSON.parse(document.querySelector(this.SELECTORS.jsonTranslations)?.textContent || '{}');
      this.#countdownInitialized = sessionStorage.getItem(this.STORAGE_KEYS.countdownInitialized) === 'true';
    } catch (error) {
      console.error("Error initializing CrossSellManager data:", error);
      return;
    }
  }

  init() {
    this.#ensureControlCrossSellHidden();
    this.#setupTriggerObserver();
    this.#setupMainObserver();
    
    // Si el carrito ya está en el DOM al cargar
    if (document.querySelector(this.SELECTORS.cartItemList)) {
      this.#handleCartChanges();
    } else {
      this.#startPeriodicCheck();
    }

    if (this.#countdownInitialized) {
        this.#previousItems = this.#getCurrentItems();
        this.#checkPhMacFamiliesMatches(this.#previousItems);
    }
    
    // Configurar listener para delegación de eventos
    document.body.addEventListener('click', this.#handleBundleAdd.bind(this));
  }
  
  // --- Lógica de Control del Cross-Sell Original ---

  #getControlCrossSell = () => document.querySelector(this.SELECTORS.controlCrossSell);
  #hideControlCrossSell = () => this.#getControlCrossSell()?.style.setProperty('display', 'none');
  #showControlCrossSell = () => this.#getControlCrossSell()?.style.setProperty('display', 'block');

  #ensureControlCrossSellHidden() {
    const control = this.#getControlCrossSell();
    if (control) {
      control.style.setProperty('display', 'none');
      return;
    }
    this.#controlCrossSellHideObserver?.disconnect();
    this.#controlCrossSellHideObserver = new MutationObserver((mutations, observer) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1) {
            const element = node.id === 'corner-cowi-related-items' ? node : node.querySelector?.(this.SELECTORS.controlCrossSell);
            if (element) {
              element.style.setProperty('display', 'none');
              observer.disconnect();
              this.#controlCrossSellHideObserver = null;
              return;
            }
          }
        }
      }
    });
    this.#controlCrossSellHideObserver.observe(document.body, { childList: true, subtree: true });
  }

  // --- Lógica de la Cuenta Atrás ---

  #initializeCountdown() {
    if (!this.#countdownInitialized) return false;
    
    const savedSeconds = sessionStorage.getItem(this.STORAGE_KEYS.countdownSeconds);
    if (savedSeconds) {
      this.#countdownSeconds = parseInt(savedSeconds, 10);
    } else {
      this.#resetCountdown();
    }
    return this.#countdownSeconds > 0;
  }

  #startCountdownForFirstTime() {
    if (this.#countdownInitialized) return;
    this.#countdownInitialized = true;
    sessionStorage.setItem(this.STORAGE_KEYS.countdownInitialized, 'true');
    this.#resetCountdown();
    this.#handleCartChanges();
  }

  #startCountdown() {
    if (this.#countdownTimer) clearInterval(this.#countdownTimer);
    if (!this.#initializeCountdown()) {
      this.#showOriginalCrossSell();
      return;
    }

    this.#countdownTimer = setInterval(() => {
      this.#countdownSeconds--;
      this.#saveCountdownState();
      this.#updateCountdownDisplay();

      if (this.#countdownSeconds <= 0) {
        this.#showOriginalCrossSell();
        this.#stopCountdown();
      }
    }, 1000);
  }

  #stopCountdown() {
    if (this.#countdownTimer) {
      clearInterval(this.#countdownTimer);
      this.#countdownTimer = null;
    }
  }
  
  #resetCountdown = () => this.#countdownSeconds = this.COUNTDOWN_DURATION;
  #saveCountdownState = () => sessionStorage.setItem(this.STORAGE_KEYS.countdownSeconds, this.#countdownSeconds.toString());
  #formatTime = (seconds) => `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
  #normalizePrice(value) {
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    if (typeof value === 'string') {
      // Elimina símbolos monetarios y espacios de miles, adapta coma decimal a punto
      const normalized = value
        .replace(/\s/g, '')
        .replace(/[^\d.,-]/g, '')
        .replace(/,(?=\d{1,2}(?:$|[^\d]))/, '.') // coma decimal típica
        .replace(/,(?=\d{3}\b)/g, '') // posible separador de miles
        .replace(/\.(?=\d{3}\b)/g, ''); // posible separador de miles con punto
      const parsed = parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  }
  
  #resolveLocaleKey() {
    // Obtiene el market crudo (p.ej. "pt-PT" o "es") y lo adapta a una clave disponible en translates
    const rawMarket = this.#phMacTranslations?.[0]?.market || '';
    const translates = this.#phMacTranslations?.[0]?.translates || {};
    if (!rawMarket) return Object.keys(translates)[0] || 'en';
    if (translates[rawMarket]) return rawMarket;
    const base = rawMarket.split('-')[0];
    if (translates[base]) return base;
    // fallback: intenta "pt" si base era "pt" con región distinta, o el primero disponible
    return Object.keys(translates)[0] || 'en';
  }
  
  #updateCountdownDisplay() {
      const countdownElement = document.querySelector(this.SELECTORS.countdownDisplay);
      const market = this.#resolveLocaleKey();
      const title = this.#phMacTranslations?.[0]?.translates?.[market]?.title || "Oferta termina en";
      if (countdownElement) {
        countdownElement.textContent = `${title} ${this.#formatTime(this.#countdownSeconds)}`;
      }
  }
  
  #showOriginalCrossSell() {
      this.#controlCrossSellHideObserver?.disconnect();
      this.#controlCrossSellHideObserver = null;
      this.#showControlCrossSell();
      this.#removeCrossSellContainer();
  }

  // --- Lógica del Contenedor Personalizado ---

  #createCrossSellContainer() {
    if (this.#phMacCrossSellContainer && document.body.contains(this.#phMacCrossSellContainer)) return;
    const anchor = document.querySelector(this.SELECTORS.cartItemListWrapper);
    if (anchor) {
      this.#phMacCrossSellContainer = document.createElement("div");
      this.#phMacCrossSellContainer.id = "ph-mac133-json-container";
      anchor.after(this.#phMacCrossSellContainer);
      this.#ensureControlCrossSellHidden();
    }
  }

  #removeCrossSellContainer() {
    this.#stopCountdown();
    this.#phMacCrossSellContainer?.remove();
    this.#phMacCrossSellContainer = null;
  }
  
  // --- Lógica del Carrito y Bundles ---
  
  #getCurrentItems() {
    const cartItemList = document.querySelector(this.SELECTORS.cartItemList);
    if (!cartItemList) return [];

    return Array.from(cartItemList.children).map(child => {
      // Intentar dataset directo
      let productId = child?.dataset?.productId || child?.getAttribute?.('data-product-id') || '';
      let variantId = child?.dataset?.variantId || child?.getAttribute?.('data-variant-id') || '';

      // Intentar buscar en descendientes si no está en el nodo raíz del item
      if (!productId) {
        const el = child.querySelector?.('[data-product-id]');
        productId = el?.getAttribute?.('data-product-id') || '';
      }
      if (!variantId) {
        const el = child.querySelector?.('[data-variant-id]');
        variantId = el?.getAttribute?.('data-variant-id') || '';
      }

      return { productId, variantId };
    });
  }

  async #fetchCartItemsFromApi() {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!response.ok) return [];
      const cart = await response.json();
      const items = Array.isArray(cart?.items) ? cart.items : [];
      return items.map(item => ({
        productId: (item.product_id ?? '').toString(),
        variantId: (item.variant_id ?? '').toString()
      }));
    } catch (e) {
      console.error('PH-MAC-0133: error obteniendo /cart.js', e);
      return [];
    }
  }

  #createBundleHTML(bundle) {
    const market = this.#resolveLocaleKey();
    if (!market) return '';

    const comparePrice = this.#normalizePrice(bundle.compare_at_price);
    const currentPrice = this.#normalizePrice(bundle.price);
    const discountPercentage = comparePrice > 0 && comparePrice > currentPrice
      ? Math.ceil(((comparePrice - currentPrice) / comparePrice) * 100)
      : 0;
    
    let translatedDescription = bundle.description || '';
    try {
        const descObj = JSON.parse(bundle.description);
        translatedDescription = descObj[market]?.translation || translatedDescription;
    } catch (e) {}

    return `
      <div class="ph-mac-bundle-item" data-bundle-id="${bundle.id}">
        <div class="ph-mac-bundle-image">
          <img src="https:${bundle.image}" alt="${bundle.title}" loading="lazy">
        </div>
        <div class="ph-mac-bundle-content">
          <h3 class="ph-mac-bundle-title">${bundle.title}</h3>
          <p class="ph-mac-bundle-description">${translatedDescription}</p>
          <button class="ph-mac-bundle-add-btn" data-bundle-id-btn="${bundle.id}">
            ${this.#phMacTranslations?.[0]?.translates?.[market]?.cta || ''}
          </button>
        </div>
        <div class="ph-mac-bundle-prices">
          <p class="ph-mac-bundle-price">${bundle.price}</p>
          ${comparePrice > 0 ? `<p class="ph-mac-bundle-compare-price">${bundle.compare_at_price}</p>` : ''}
          ${discountPercentage > 0 ? `<span class="ph-mac-bundle-price-saving">-${discountPercentage}%</span>` : ''}
        </div>
      </div>`;
  }

  #getAvailableBundles(bundles, cartItems) {
    const cartIds = cartItems.flatMap(item => [parseInt(item.productId), parseInt(item.variantId)]).filter(id => !isNaN(id));
    return bundles.filter(bundle => bundle && !cartIds.includes(parseInt(bundle.id)));
  }

  #renderBundlesInContainer(bundles, cartItems) {
    // Asegurar inicialización de la cuenta atrás aunque el trigger no haya cambiado de estado
    if (!this.#countdownInitialized) {
        this.#startCountdownForFirstTime();
    }

    this.#createCrossSellContainer();
    if (!this.#phMacCrossSellContainer) return;
    
    const availableBundles = this.#getAvailableBundles(bundles, cartItems);
    const market = this.#resolveLocaleKey();
    if (!market) return;

    if (availableBundles.length === 0) {
      if (this.#getControlCrossSell()) {
        this.#showOriginalCrossSell();
      } else {
        this.#phMacCrossSellContainer.innerHTML = `<div class="ph-mac-no-bundles"><p>${this.#phMacTranslations[0].translates[market].noProducts}</p></div>`;
      }
      return;
    }

    const title = this.#phMacTranslations?.[0]?.translates?.[market]?.title || '';
    this.#phMacCrossSellContainer.innerHTML = `
      <div class="ph-mac-cross-sell-section">
        <h2 class="ph-mac-section-title" id="${this.SELECTORS.countdownDisplay.slice(1)}">${title} ${this.#formatTime(this.#countdownSeconds)}</h2>
        <div class="ph-mac-bundles-container">
          ${availableBundles.map(bundle => this.#createBundleHTML(bundle)).join("")}
        </div>
      </div>`;

    this.#startCountdown();
  }

  #renderComodinBundles(cartItems) {
    const comodinFamily = this.#phMacFamilies.find(family => family.priority === 99);
    const validBundles = comodinFamily?.bundles?.filter(Boolean) || [];

    if (validBundles.length > 0) {
      this.#renderBundlesInContainer(validBundles, cartItems);
      return true;
    }
    
    this.#removeCrossSellContainer();
    return false;
  }

  #checkPhMacFamiliesMatches(cartItems) {
    const cartVariantIds = cartItems.map(item => parseInt(item.variantId)).filter(id => !isNaN(id));
    if (cartVariantIds.length === 0) {
      this.#removeCrossSellContainer();
      return;
    }

    const sortedFamilies = this.#phMacFamilies
      .filter(family => family.priority >= 1 && family.priority <= 6)
      .sort((a, b) => a.priority - b.priority);

    for (const family of sortedFamilies) {
      const hasMatch = family.product_variants.some(variant => cartVariantIds.includes(variant));
      if (hasMatch) {
        const validBundles = family.bundles.filter(Boolean);
        if (validBundles.length > 0) {
          this.#renderBundlesInContainer(validBundles, cartItems);
        } else {
          this.#renderComodinBundles(cartItems);
        }
        return; // Salir tras encontrar la primera familia con mayor prioridad
      }
    }

    this.#renderComodinBundles(cartItems); // Fallback si ninguna familia coincide
  }
  
  // --- Observadores y Handlers ---

  #debounceExecution(callback, delay = 150) {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(callback, delay);
  }

  #handleCartChanges = () => {
    const currentItems = this.#getCurrentItems();
    if (currentItems.length === 0) {
        this.#removeCrossSellContainer();
        this.#previousItems = [];
        return;
    }

    // Si no hay variantId válidos en el DOM, hacer fallback al API del carrito
    const hasValidVariant = currentItems.some(it => Number.isFinite(parseInt(it.variantId, 10)));
    if (!hasValidVariant) {
        this.#fetchCartItemsFromApi().then(apiItems => {
            if (!apiItems.length) {
                this.#removeCrossSellContainer();
                this.#previousItems = [];
                return;
            }
            this.#previousItems = [...apiItems];
            this.#checkPhMacFamiliesMatches(apiItems);
        }).catch(console.error);
        return;
    }

    this.#previousItems = [...currentItems];
    this.#checkPhMacFamiliesMatches(currentItems);
  }
  
  #handleBundleAdd(event) {
    const button = event.target.closest(this.SELECTORS.bundleAddBtn);
    if (!button) return;
    
    event.preventDefault();
    const bundleId = button.dataset.bundleIdBtn;
    if (bundleId) {
        fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: bundleId, quantity: 1 })
        }).catch(console.error);
    }
  }
  
  #setupTriggerObserver() {
    const triggerElement = document.querySelector(this.SELECTORS.cartTrigger);
    if (!triggerElement) {
        setTimeout(() => this.#setupTriggerObserver(), 1000);
        return;
    }
    
    this.#triggerObserver = new MutationObserver(mutations => {
        if (mutations.some(m => m.target.classList.contains('pointer-events-auto'))) {
            this.#startCountdownForFirstTime();
        }
    });

    this.#triggerObserver.observe(triggerElement, { attributes: true, attributeFilter: ['class'] });
    
    // Comprobación inicial
    if (triggerElement.classList.contains('pointer-events-auto')) {
        this.#startCountdownForFirstTime();
    }
  }

  #setupCartObserver() {
    this.#cartObserver?.disconnect();
    const cartItemList = document.querySelector(this.SELECTORS.cartItemList);
    if (!cartItemList) return;

    this.#cartObserver = new MutationObserver(() => {
        this.#debounceExecution(this.#handleCartChanges);
    });
    this.#cartObserver.observe(cartItemList, { childList: true });
  }
  
  #setupMainObserver() {
    this.#containerObserver = new MutationObserver(mutations => {
        let cartChanged = false;
        for (const mutation of mutations) {
            const addedCart = Array.from(mutation.addedNodes).some(node => node.nodeType === 1 && node.querySelector(this.SELECTORS.cartItemListWrapper));
            const removedCart = Array.from(mutation.removedNodes).some(node => node.nodeType === 1 && node.id === 'corner-cowi-cart-item-list-wrapper');

            if (removedCart) {
                this.#removeCrossSellContainer();
                this.#startPeriodicCheck();
                return;
            }
            if (addedCart) {
                this.#stopPeriodicCheck();
                cartChanged = true;
                // Inicializar cuenta atrás al detectar el carrito en el DOM
                this.#startCountdownForFirstTime();
            }
        }
        if (cartChanged) {
            this.#setupCartObserver();
            this.#debounceExecution(this.#handleCartChanges, 200);
        }
    });

    this.#containerObserver.observe(document.body, { childList: true, subtree: true });
  }

  // --- Fallback con chequeo periódico ---
  
  #startPeriodicCheck() {
    this.#stopPeriodicCheck();
    this.#periodicCheckInterval = setInterval(() => {
      if (document.querySelector(this.SELECTORS.cartItemListWrapper)) {
        this.#stopPeriodicCheck();
        // Inicializar cuenta atrás cuando detectamos el carrito mediante el chequeo periódico
        this.#startCountdownForFirstTime();
        this.#setupCartObserver();
        this.#handleCartChanges();
      }
    }, 300);
  }

  #stopPeriodicCheck() {
    clearInterval(this.#periodicCheckInterval);
    this.#periodicCheckInterval = null;
  }
}

// Punto de entrada: Instanciar e inicializar la clase cuando el DOM esté listo.
document.addEventListener("DOMContentLoaded", () => {
  const manager = new CrossSellManager();
  manager.init();
});